'use client';

import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import type { HpMarketingAd as HpMarketingAdData, HpMarketingAdMountCallback, HpMarketingCardData } from '../../../types/hp-marketing-ad';
import { LeagueCard } from '../../global/Cards/LeagueCard';
import { MyClubCard } from '../../global/Cards/MyClubCard';
import { TeamLeagueCard } from '../../global/Cards/TeamLeagueCard';
import { TournamentCard } from '../../global/Cards/TournamentCard';
import { ImpressionWrapper } from '../../impressions/impression-wrapper';

/**
 * Bridge between the ad's pre-fetched card payload and the platform card component (`<TournamentCard>` /
 * `<LeagueCard>`). Centralized so the kind-discriminator lives in one place — adding `club` / `team-league`
 * later is a matter of extending the type union and adding a case here.
 *
 * `title` comes straight from the source entity's data — listing ads don't carry their own accessibility
 * title (the BE listing-ad payload omits it), so the card title is the only source of truth here.
 *
 * Click-through wrapping:
 *   - `<TournamentCard>` has a built-in `customCardHref` prop, so we pass through directly.
 *   - `<LeagueCard>` doesn't link itself, so when a `customCardHref` is set we wrap the card in an anchor.
 *   - Every card gets `nativeAnchor`: the click-through must be a full document navigation, not a Next
 *     client-side transition. The ad URL is a same-origin URL carrying Everflow parameters (`oid` /
 *     `affid`), and the Everflow landing middleware only records the click, sets the cookies and cleans
 *     the URL on document requests — a `<Link>` would skip all of that and leave the parameters in the
 *     address bar. Static (image) ads below already render a plain `<a>`.
 */
export type HpMarketingCardRenderer = (card: HpMarketingCardData) => React.ReactNode;

export function renderCardForAd(card: HpMarketingCardData): React.ReactNode {
	switch (card.kind) {
		case 'tournament':
			// The slim card payload only carries logo / title / location / dates — all other `TournamentCard`
			// required props are placeholders that the suppression flags neutralize: `shouldHideBadge` hides
			// the status badge that would otherwise compute to "Free", `hideRegisteredPlayers` hides the
			// "0 players" line that would otherwise render from the placeholder count.
			return (
				<TournamentCard
					id={card.id}
					slug={card.slug}
					title={card.title}
					logo={card.logo}
					dateFrom={card.dateFrom}
					dateTo={card.dateTo}
					location={card.location}
					currency=""
					price={0}
					registrationCount={0}
					isRegistrationClosed={false}
					hideRegisteredPlayers
					shouldHideBadge
					tournamentsAppBaseURL={card.tournamentsAppBaseURL}
					customCardHref={card.customCardHref}
					openInNewTab={card.openInNewTab}
					nativeAnchor
					// Always hide the bottom badge/registration-count row inside the rotator — the rotator
					// already passes `shouldHideBadge` + `hideRegisteredPlayers`, which guarantees the row
					// would otherwise render as an empty wrapper. Per-ad opt-out via `card.hideBottomRow`
					// honored (set it `false` to force the row back on for a specific ad).
					hideBottomRow={card.hideBottomRow ?? true}
					isTournamentsApp
				/>
			);
		case 'league':
			// Same suppression model as the tournament variant — placeholders neutralized by
			// `shouldHideBadge` + `hideRegisteredPlayers`. `customCardHref` is now built into `LeagueCard`
			// (matching `TournamentCard`'s pattern) — the card wraps itself in `CustomLink` when set,
			// no external anchor wrapper needed here. `clubTitle` shows the parent club name between the
			// league title and location lines.
			return (
				<LeagueCard
					id={card.id}
					slug={card.slug}
					title={card.title}
					clubTitle={card.clubTitle}
					logo={card.logo}
					location={card.location}
					dateFrom={card.dateFrom}
					dateTo={card.dateTo}
					currency=""
					formatTag=""
					price="0"
					registrationCount={0}
					hideRegisteredPlayers
					shouldHideBadge
					shouldHideStartTime
					customCardHref={card.customCardHref}
					openInNewTab={card.openInNewTab}
					nativeAnchor
					// Same rationale as the tournament case above — bottom row would render empty given the
					// rotator's flags; force-hide with per-ad opt-out via `card.hideBottomRow`.
					hideBottomRow={card.hideBottomRow ?? true}
					hideLocation={card.hideLocation}
				/>
			);
		case 'org':
			// `MyClubCard` doesn't accept hide-count flags — per design we show the real member count.
			// `customCardHref` is built into the card (same pattern as the other three).
			return (
				<MyClubCard
					id={card.id}
					slug={card.slug}
					title={card.title}
					logo={card.logo}
					location={card.location}
					memberCount={card.memberCount}
					customCardHref={card.customCardHref}
					openInNewTab={card.openInNewTab}
					nativeAnchor
				/>
			);
		case 'team-league': {
			// `TeamLeagueCard` is typed against a `league` prop (object), not flat fields. We construct a
			// minimal `League` literal from the slim summary — `playerCount` / `isSeasonActive` /
			// `seasonStandingsExist` get safe defaults, and `hidePlayersCount` / `hideTeamsCount` are forced
			// `true` so neither the player-count chip nor the team-count text renders for an ad card.
			//
			// Sizing: by default `TeamLeagueCard` has a hardcoded `w-[224px] h-[322px]` outer box, which
			// doesn't play with the rotator's grid cell. Pass `isSearchCard` to drop the fixed width, and
			// override the fixed height + add responsive min/max width via `className` (Tailwind's `!`
			// modifier wins over the card's defaults) so the card fills the grid cell like the tournament
			// and league variants do.
			return (
				<TeamLeagueCard
					league={{
						uuid: card.id,
						slug: card.slug,
						organizationUuid: card.organizationUuid,
						title: card.title,
						logo: card.logo,
						location: card.location,
						startDatetime: card.dateFrom,
						endDatetime: card.dateTo,
						playerCount: 0,
						isFavourite: false,
						isSeasonActive: false,
						seasonStandingsExist: false,
						hidePlayersCount: true,
						hideTeamsCount: true
					}}
					teamLeaguesBaseUrl={card.teamLeaguesBaseUrl}
					customCardHref={card.customCardHref}
					openInNewTab={card.openInNewTab}
					nativeAnchor
					hideStartDateLine={card.hideStartDateLine}
					isSearchCard
					// `isHeightFitContent` switches the inner content block from a hardcoded `h-[200px]` to
					// `h-fit` so it sizes to its actual content — otherwise we get a 200px tall empty area
					// under the title/location strip when fewer fields are shown.
					isHeightFitContent
					className="!h-full !w-full min-[500px]:!min-w-[224px] min-[500px]:!max-w-[286px]"
				/>
			);
		}
		default: {
			const exhaustive: never = card;
			throw new Error(`Unknown listing-ad card kind: ${(exhaustive as { kind: string }).kind}`);
		}
	}
}

export interface HpMarketingAdProps {
	/**
	 * Ordered list of ads to rotate through. The consumer is responsible for fetching from BE and normalizing
	 * into the {@link HpMarketingAdData} shape. If the list is empty, the component renders nothing — the
	 * caller should treat that as "no ad" and lay out its slot accordingly (e.g. let other cards take the space).
	 */
	ads: HpMarketingAdData[];
	/**
	 * `SystemModuleType` numeric value used when reporting VIEW / CLICK engagement for the currently-displayed
	 * ad. Per-placement (e.g. tournament homepage + search page both use `SEARCH_CARD_AD_MODULE_TYPE`;
	 * a future Pickleball Central placement would pass `PICKLEBALL_CENTRAL_MODULE_TYPE`).
	 */
	moduleFor: number;
	/**
	 * Optional client-side hook fired once when the component first mounts. Wire your app-specific server
	 * action (impression / engagement / audit log) here. Errors thrown by the callback are swallowed so a
	 * tracking failure can't surface as a UI error.
	 */
	onMount?: HpMarketingAdMountCallback;
	/** Class applied to the outer slot (`ImpressionWrapper`'s div). Use to set slot dimensions / spacing. */
	className?: string;
	/**
	 * How the desktop (`>= md`) image sizes itself. `'fill'` (default) absolutely positions the image to
	 * fill a parent-driven slot height — correct inside grid cells where sibling cards set the row height.
	 * `'flow'` keeps the image in normal flow so it sets its own height from its aspect ratio — required in
	 * full-width stacked sections with no external height source (the fill image collapses to 0 there).
	 */
	desktopLayout?: 'fill' | 'flow';
	/**
	 * Overrides how a listing (card) ad's body is rendered. Injected rather than resolved here because the
	 * v2 module cards live in `@pickleballinc/features`, which already depends on this package — importing
	 * them would close a dependency cycle. The consuming app owns the decision (typically its own
	 * `FEATURE_FLAGS.ENABLE_NEW_CARDS`), matching how every other v1/v2 card switch in the codebase works.
	 *
	 * Return `undefined` (or omit the prop) to fall back to the built-in v1 rendering, so a renderer can
	 * opt out per card kind without having to reimplement the others.
	 */
	renderCard?: HpMarketingCardRenderer;
}

/**
 * IntersectionObserver threshold above which we consider the ad "visible enough" to count toward its
 * `displayDurationSeconds`. 50% of the element's bounding box must overlap the viewport — matches the
 * `ImpressionWrapper` VIEW threshold so rotation timing and impression counting agree on what "visible" means.
 */
const VIEWPORT_VISIBILITY_THRESHOLD = 0.5;

/**
 * Rotating search-card advertisement.
 *
 * - On `< md` breakpoints renders `mobileImageUrl`; at `>= md` renders `desktopImageUrl` (both elements stay
 *   in the DOM, hidden via Tailwind — SSR-safe, no client-side viewport detection).
 * - With a single ad in the array, no timer is set (nothing to advance to).
 * - The wrapping anchor's `target` follows `urlType` — `URL_TYPE_EXTERNAL` opens in a new tab.
 *
 * **Rotation accounts for actual visibility, not wall-clock time.** A scroll-away, tab switch, or window
 * minimize pauses the timer; returning to the ad resumes it from where it left off. So each ad gets its full
 * `displayDurationSeconds` of *actually-seen* exposure before the rotator advances to the next entry.
 */
export function HpMarketingAd({ ads, moduleFor, onMount, className, desktopLayout = 'fill', renderCard }: HpMarketingAdProps) {
	const [index, setIndex] = useState(0);

	// Container element (anchor or div) — held in state so the IntersectionObserver effect can re-attach if
	// the rendered element changes (e.g. `websiteUrl` flips between truthy / falsy across ads).
	const [containerEl, setContainerEl] = useState<Element | null>(null);

	const [isInViewport, setIsInViewport] = useState(false);
	const [isPageVisible, setIsPageVisible] = useState(true);

	const hasFiredMountRef = useRef(false);
	const accumulatedMsRef = useRef(0);

	const isActuallyVisible = isInViewport && isPageVisible;

	// Fire the mount callback exactly once per component instance. Ref guard protects against React 18 Strict
	// Mode double-invocation in dev and against `onMount` being re-created on every render by the consumer.
	useEffect(() => {
		if (hasFiredMountRef.current) return;
		hasFiredMountRef.current = true;
		if (!onMount) return;
		void Promise.resolve(onMount(ads)).catch(() => {
			// Intentionally swallowed — consumers should log inside their action.
		});
	}, [ads, onMount]);

	// Observe whether the element is in the viewport. SSR / no-IO fallback: treat as visible so rotation
	// still works in environments without IntersectionObserver (older browsers, tests).
	useEffect(() => {
		if (!containerEl) return;
		if (typeof IntersectionObserver === 'undefined') {
			setIsInViewport(true);
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				setIsInViewport(!!entry?.isIntersecting);
			},
			{ threshold: VIEWPORT_VISIBILITY_THRESHOLD }
		);
		observer.observe(containerEl);
		return () => observer.disconnect();
	}, [containerEl]);

	// Page visibility (browser tab, window minimize). A backgrounded page counts as "not visible" even when
	// the ad would otherwise be in the viewport on screen.
	useEffect(() => {
		if (typeof document === 'undefined') return;
		const update = () => setIsPageVisible(document.visibilityState === 'visible');
		update();
		document.addEventListener('visibilitychange', update);
		return () => document.removeEventListener('visibilitychange', update);
	}, []);

	// Reset the cumulative-visible-time accumulator when the index advances to a new ad.
	useEffect(() => {
		accumulatedMsRef.current = 0;
	}, [index]);

	// Rotation timer — only ticks while actually visible. When visibility hides, the effect's cleanup adds
	// the time the ad was actually shown to `accumulatedMsRef`. When visibility resumes, a fresh `setTimeout`
	// is scheduled for the REMAINING duration (`durationMs - accumulated`). Net effect: cumulative visible
	// time governs when we advance, not the wall clock.
	useEffect(() => {
		if (ads.length <= 1) return;
		const current = ads[index];
		if (!current) return;
		if (!isActuallyVisible) return;

		const durationMs = Math.max(1, current.displayDurationSeconds) * 1000;
		const remainingMs = Math.max(0, durationMs - accumulatedMsRef.current);
		const startedAt = Date.now();

		const timer = setTimeout(() => {
			setIndex((prev) => (prev + 1) % ads.length);
		}, remainingMs);

		return () => {
			clearTimeout(timer);
			accumulatedMsRef.current += Date.now() - startedAt;
		};
	}, [ads, index, isActuallyVisible]);

	if (ads.length === 0) return null;
	const current = ads[index] ?? ads[0];
	if (!current) return null;

	// `accessibilityTitle` is the screen-reader name; `accessibilityDescription` (optional) gets attached via
	// `aria-describedby` through a visually-hidden `<span>`, so SR users hear the longer context after the
	// short title. Sighted users never see the description.
	const descriptionId = current.accessibilityDescription ? `hp-marketing-ad-desc-${current.uuid}` : undefined;
	const descriptionNode = current.accessibilityDescription ? (
		<span id={descriptionId} className="sr-only">
			{current.accessibilityDescription}
		</span>
	) : null;

	// Listing (card) ad branch — render a real platform card (e.g. `<TournamentCard>`) instead of the static
	// `<img>` pair. The rotator slot, `ImpressionWrapper`, and rotation timing are all reused; only the
	// rendered body differs. The card sizing (max-w-[286px]) already matches a tournament card width, so the
	// slot fits both variants cleanly.
	//
	// Accessibility nodes (`descriptionNode`, `accessibilityTitle`) are intentionally NOT rendered here — the
	// BE listing-ad payload doesn't carry those fields, and the inner card already exposes its own title and
	// link semantics to screen readers via its href + visible content.
	if (current.card) {
		return (
			<ImpressionWrapper moduleId={current.hpMarketingUuid} moduleFor={moduleFor} className={`md:h-full${className ? ` ${className}` : ''}`}>
				<div ref={setContainerEl} className="block h-full w-full">
					{renderCard?.(current.card) ?? renderCardForAd(current.card)}
				</div>
			</ImpressionWrapper>
		);
	}

	// Sizing has to branch by breakpoint because no single rule satisfies both grid contexts:
	//   - Mobile (`< md`): single-column grid. The ad has no siblings in its row to drive height, so the
	//     image MUST size itself via `h-auto w-full`. Absolute positioning collapses the cell to 0 here.
	//   - Desktop (`>= md`): multi-column grid alongside cards. The ad must NOT dictate row height — at the
	//     image's 4:5-ish portrait aspect, a full-column-width render is taller than the neighboring
	//     tournament / club / league cards. So on desktop we position the image absolutely (zero natural
	//     height contribution), and let the cards drive the row track size; `md:h-full` propagates that
	//     row height down to the absolute-positioned image which fills it via `object-contain`.
	const isFlowDesktop = desktopLayout === 'flow';
	const inner = (
		<>
			{descriptionNode}
			{/* Mobile image (`< md`): in-flow, fills cell width and sets the natural cell height via `h-auto`.
			    Rounded corners come from the outer wrapper's `overflow-hidden rounded-lg`. */}
			<img src={current.mobileImageUrl} alt={current.accessibilityTitle} className="block h-auto w-full rounded-lg md:hidden" loading="eager" />
			{isFlowDesktop ? (
				<img
					src={current.desktopImageUrl}
					alt={current.accessibilityTitle}
					className="hidden h-auto w-full rounded-lg md:block"
					loading="eager"
				/>
			) : (
				/* Desktop image (`>= md`): the absolutely-positioned wrapper `<div>` owns the cell-fill positioning
				   so the `<img>` inside only worries about how to render within its parent. Positioning concerns
				   on the wrapper, rendering concerns on the image — the outer `overflow-hidden rounded-lg` then
				   reliably clips the image to a rounded shape. */
				<div className="hidden md:absolute md:inset-0 md:flex md:h-full md:w-full md:items-center md:justify-center md:overflow-hidden">
					<img
						src={current.desktopImageUrl}
						alt={current.accessibilityTitle}
						className="max-h-full max-w-full rounded-lg"
						loading="eager"
					/>
				</div>
			)}
		</>
	);

	// `ImpressionWrapper` instance persists across rotations (no `key` prop). The wrapper dedupes VIEW + CLICK
	// per `(moduleFor, moduleId)` internally, so cycling through the same N ads any number of times produces
	// at most N VIEWs and N CLICKs over the page session — matches `FeaturedTournamentCard` semantics
	// (scroll in / out / in counts as one VIEW). When `moduleId` flips on rotation, the wrapper's effect
	// re-runs: an unseen id attaches a fresh observer and fires; an already-fired id skips observation.
	const isExternal = current.urlType === 'URL_TYPE_EXTERNAL';

	// On desktop the inner wrapper needs `relative` (so the absolute-positioned desktop image resolves
	// `inset-0` against it) plus `h-full w-full` (so it stretches to fill the grid cell). On mobile we leave
	// it as default flow so the visible mobile image dictates the natural height. `rounded-lg overflow-hidden`
	// rounds the wrapper too so the visible boundary stays rounded regardless of whether the image fills the
	// box (e.g. if a future change uses `object-contain` empty space the wrapper's rounded shape still shows).
	const innerWrapperClassName = clsx('block overflow-hidden rounded-lg', {
		'md:relative md:h-full md:w-full': !isFlowDesktop
	});

	const body = current.websiteUrl ? (
		<a
			ref={setContainerEl}
			href={current.websiteUrl}
			target={isExternal ? '_blank' : '_self'}
			rel={isExternal ? 'noopener noreferrer' : undefined}
			aria-label={current.accessibilityTitle}
			aria-describedby={descriptionId}
			className={innerWrapperClassName}
		>
			{inner}
		</a>
	) : (
		<div ref={setContainerEl} aria-label={current.accessibilityTitle} aria-describedby={descriptionId} className={innerWrapperClassName}>
			{inner}
		</div>
	);

	// Propagate `md:h-full` through `ImpressionWrapper` so its outer div also stretches at the desktop
	// breakpoint — required for the `md:h-full` chain on the inner wrapper to resolve against a defined
	// parent height (fill layout only — flow sizes itself). Any consumer-supplied className wins on overlap
	// (last in the string).
	return (
		<ImpressionWrapper
			moduleId={current.hpMarketingUuid}
			moduleFor={moduleFor}
			className={clsx({ 'md:h-full': !isFlowDesktop }, className) || undefined}
		>
			{body}
		</ImpressionWrapper>
	);
}
